import { useEffect, useState } from 'react';
// import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { useIsMutating } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';
import { scrollTo } from 'seamless-scroll-polyfill';
import { getCountriesOptions, genderOptions, passwordRegEx } from '@app/constants';
// import { useHospitalsInfinite } from '@data/hooks/matching/hospitals';
import { isPhoneValid } from '@app/utils';
import { isValidEmailStrict } from '@data/utils/validation';
import {
  Button,
  AntdCheckbox,
  AntdCol,
  AntdConfigProvider,
  AntdForm,
  AntdGrid,
  AntdRow,
  AsyncSelect,
  useTheme,
  AntdTypography,
  DateSelect,
  PhoneInput,
} from '@ui';
import { Select, Input, InputPassword } from '../../components';
import { UserInfoFormProps } from './index';

import './sign-up.css';

const { useBreakpoint } = AntdGrid;
const { Text } = AntdTypography;

const UserInfoForm = ({
  handleBack,
  form,
  initialValues,
  anonymousSignUp,
}: // addHospital,
// setAddHospital,
UserInfoFormProps) => {
  const { t } = useTranslation();
  const { md } = useBreakpoint();
  const isMutating = !!useIsMutating();
  const { textColor, textColorDark, bgColorLight } = useTheme();

  const [country, setCountry] = useState('');
  // const [hospitalCountry, setHospitalCountry] = useState('');

  // const { hospitalsOptions, hospitalsAreLoading, fetchNextPage, searchString, handleSearchChange } =
  //   useHospitalsInfinite();

  // const onCreateCustomHospitalClick = () => {
  //   setAddHospital(true);
  //   form.setFieldValue(['customHospital', 'name'], searchString);
  //   handleSearchChange('');
  //   form.setFieldValue('hospital', null);
  // };

  // const onCancelCreateCustomHospitalClick = () => {
  //   setAddHospital(false);
  //   form.setFieldValue('customHospital', null);
  // };

  useEffect(() => {
    scrollTo(window, { top: 0, behavior: 'smooth' });
  }, []);

  return (
    <AntdForm
      name="user-info-form"
      form={form}
      layout="vertical"
      className="sing-up-form"
      disabled={isMutating}
      autoComplete="off"
      initialValues={initialValues}
      data-testid="user-info-form"
      size={md ? 'middle' : 'large'}
      scrollToFirstError={true}
    >
      <Text className="text-base-500 flex mb-4">
        {t('sign_up_page.create_account_to_secure_data')}:
      </Text>
      <AntdForm.Item
        label={<Text className="font-semibold">{t('common.email_field_label')}</Text>}
        name="email"
        rules={[
          { required: true, message: t('validations.required') },
          () => ({
            validator(_, value) {
              if (value && !isValidEmailStrict(value))
                return Promise.reject(new Error(t('validations.email')));
              return Promise.resolve();
            },
          }),
        ]}
        validateTrigger="onBlur"
      >
        <Input data-testid="email-input" className="h-[48px] sm:h-auto" inputMode="email" />
      </AntdForm.Item>

      <AntdForm.Item
        label={<Text className="font-semibold">{t('common.password')}</Text>}
        name="password"
        rules={[
          {
            message: t('validations.password'),
            pattern: passwordRegEx,
            required: true,
          },
        ]}
      >
        <InputPassword data-testid="password-input" className="h-[48px] sm:h-auto" />
      </AntdForm.Item>

      <AntdForm.Item
        label={<Text className="font-semibold">{t('common.confirm_password')}</Text>}
        name="confirmPassword"
        dependencies={['password']}
        rules={[
          {
            required: true,
            message: t('validations.confirm_password'),
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error(t('validations.passwords_not_match')));
            },
          }),
        ]}
      >
        <InputPassword data-testid="confirm-password-input" className="h-[48px] sm:h-auto" />
      </AntdForm.Item>

      <AntdForm.Item
        name="phoneNumber"
        label={t('common.phone_number')}
        className="mb-4 w-full font-semibold"
        rules={[
          {
            validator: (_, val: string) =>
              val && !isPhoneValid(val)
                ? Promise.reject(t('patient:validations.phone_number_is_invalid'))
                : Promise.resolve(),
          },
        ]}
      >
        <PhoneInput
          placeholder="+32 999 99 99 99"
          className="[&>input]:w-full custom-phone-input rounded-large"
          disabled={isMutating}
          isPublicPage
        />
      </AntdForm.Item>

      <AntdForm.Item
        label={<Text className="font-semibold">{t('sign_up_page.name')}</Text>}
        name="firstName"
        rules={[{ required: true, type: 'string', message: t('validations.required') }]}
      >
        <Input data-testid="first-name-input" className="h-[48px] sm:h-auto" />
      </AntdForm.Item>
      <AntdForm.Item
        label={<Text className="font-semibold">{t('sign_up_page.surname')}</Text>}
        name="lastName"
        rules={[{ required: true, type: 'string', message: t('validations.required') }]}
      >
        <Input data-testid="last-name-input" className="h-[48px] sm:h-auto" />
      </AntdForm.Item>

      {!anonymousSignUp && (
        <>
          <Text className="mb-4 text-base-500 flex">
            {t('sign_up_page.provide_basic_info_about_you')}:
          </Text>
          <AntdConfigProvider theme={{ token: { colorLink: textColor } }}>
            <AntdForm.Item
              label={<Text className="font-semibold">{t('sign_up_page.birth_date')}</Text>}
              name="birthDate"
              className="sing-up-form-item w-full"
              rules={[
                {
                  required: true,
                  type: 'date',
                  message: '',
                },
                {
                  validator: (_, val) =>
                    new Date() > new Date(val)
                      ? Promise.resolve()
                      : Promise.reject(t('validations.birth_date')),
                },
              ]}
            >
              <DateSelect
                handleChange={(val) => {
                  form.setFieldValue('birthDate', val);
                  if (val) form.validateFields(['birthDate']);
                }}
                handleSetError={(val: string) =>
                  form.setFields([{ name: 'birthDate', errors: val ? [val] : [] }])
                }
                className="h-[48px] sm:h-auto"
              />
            </AntdForm.Item>
          </AntdConfigProvider>
          <AntdForm.Item
            label={<Text className="font-semibold">{t('sign_up_page.gender')}</Text>}
            name="gender"
            rules={[
              () => ({
                validator(_, value) {
                  if (!value) {
                    return Promise.reject(new Error(t('validations.required')));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
            className="sing-up-form-item"
          >
            <Select
              className="w-full flex h-[48px] sm:h-auto [&>.ant-select-selector]:rounded-large"
              data-testid="gender-select"
              placement="topLeft"
              options={genderOptions()}
            />
          </AntdForm.Item>

          <AntdRow justify="space-between" className={`${!md ? 'flex flex-col' : ''}`}>
            <AntdCol xs={24} md={8}>
              <AntdForm.Item
                label={<Text className="font-semibold">{t('sign_up_page.zip_code')}</Text>}
                name="zipCode"
                rules={[{ required: true, type: 'string', message: t('validations.required') }]}
                className="sing-up-form-item"
              >
                <Input
                  data-testid="zip-code-input"
                  className="rounded-large h-[48px] sm:h-auto"
                  inputMode="numeric"
                />
              </AntdForm.Item>
            </AntdCol>
            <AntdCol xs={24} md={15}>
              <AntdForm.Item
                label={<Text className="font-semibold">{t('sign_up_page.country')}</Text>}
                name="country"
                rules={[
                  { required: true, message: '' },
                  () => ({
                    validator(_, value) {
                      if (!value) {
                        return Promise.reject(new Error(t('validations.required')));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
                className="sing-up-form-item"
              >
                <AsyncSelect
                  isSearchable
                  isClearable
                  options={getCountriesOptions()}
                  className="w-full"
                  menuShouldScrollIntoView={true}
                  captureMenuScroll={true}
                  inputId="country-select"
                  isDisabled={isMutating}
                  placeholder={t('common.type_to_search')}
                  onInputChange={setCountry}
                  value={country}
                  menuIsOpen={country?.length >= 2}
                  mobileHeight={48}
                  aria-label="country-select"
                />
              </AntdForm.Item>
            </AntdCol>
          </AntdRow>
        </>
      )}
      {/* <AntdRow justify="space-between">
            <AntdCol xs={24} md={24}>
              {addHospital ? (
                <AntdRow className="my-1 p-4 rounded-lg" style={{ backgroundColor: bgColorLight }}>
                  <div className="flex w-full items-center justify-between mb-2">
                    <span className="font-medium">{t('sign_up_page.add_hospital')}</span>
                    <Button
                      type="text"
                      icon={<CloseOutlined />}
                      onClick={onCancelCreateCustomHospitalClick}
                      style={{ color: textColor }}
                    >
                      {t('common.cancel')}
                    </Button>
                  </div>
                  <AntdCol xs={24} md={24}>
                    <AntdRow>
                      <AntdCol xs={24} md={24}>
                        <AntdForm.Item
                          label={t('sign_up_page.hospital')}
                          name={['customHospital', 'name']}
                          rules={[
                            { required: true, message: '' },
                            () => ({
                              validator(_, value) {
                                if (!value) {
                                  return Promise.reject(new Error(t('validations.required')));
                                }
                                return Promise.resolve();
                              },
                            }),
                          ]}
                          className="sing-up-form-item"
                        >
                          <AntdInput data-testid="hospital-name" />
                        </AntdForm.Item>
                      </AntdCol>
                    </AntdRow>
                    <AntdRow justify="space-between">
                      <AntdCol xs={24} md={8}>
                        <AntdForm.Item
                          initialValue={form.getFieldValue('zipCode')}
                          label={t('sign_up_page.zip_code')}
                          name={['customHospital', 'zip']}
                          rules={[
                            { required: true, type: 'string', message: t('validations.required') },
                          ]}
                          className="sing-up-form-item"
                        >
                          <AntdInput data-testid="hospital-zip-code" inputMode="numeric" />
                        </AntdForm.Item>
                      </AntdCol>
                      <AntdCol xs={24} md={15}>
                        <AntdForm.Item
                          initialValue={form.getFieldValue('country')}
                          label={t('sign_up_page.country')}
                          name={['customHospital', 'country']}
                          rules={[
                            { required: true, message: '' },
                            () => ({
                              validator(_, value) {
                                if (!value) {
                                  return Promise.reject(new Error(t('validations.required')));
                                }
                                return Promise.resolve();
                              },
                            }),
                          ]}
                          className="sing-up-form-item"
                        >
                          <AsyncSelect
                            isSearchable
                            isClearable
                            options={getCountriesOptions()}
                            className="w-full"
                            menuShouldScrollIntoView={true}
                            captureMenuScroll={true}
                            inputId="hospital-country-select"
                            aria-label="hospital-country-select"
                            isDisabled={isMutating}
                            placeholder={t('common.type_to_search')}
                            onInputChange={setHospitalCountry}
                            value={hospitalCountry}
                            menuIsOpen={hospitalCountry?.length >= 2}
                            id="hospital-country-select"
                          />
                        </AntdForm.Item>
                      </AntdCol>
                    </AntdRow>
                    <AntdRow>
                      <AntdCol xs={24} md={24}>
                        <AntdForm.Item
                          label={t('sign_up_page.city')}
                          name={['customHospital', 'city']}
                          rules={[
                            { required: true, message: '' },
                            () => ({
                              validator(_, value) {
                                if (!value) {
                                  return Promise.reject(new Error(t('validations.required')));
                                }
                                return Promise.resolve();
                              },
                            }),
                          ]}
                          className="sing-up-form-item"
                        >
                          <AntdInput data-testid="hospital-city" />
                        </AntdForm.Item>
                      </AntdCol>
                    </AntdRow>
                  </AntdCol>
                </AntdRow>
              ) : (
                <AntdForm.Item
                  label={t('sign_up_page.hospital')}
                  name="hospital"
                  rules={[
                    { required: true, message: '' },
                    () => ({
                      validator(_, value) {
                        if (!value) {
                          return Promise.reject(new Error(t('validations.required')));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                  className="sing-up-form-item"
                >
                  <AsyncSelect
                    isSearchable
                    isClearable
                    isLoading={hospitalsAreLoading}
                    options={hospitalsOptions}
                    className="w-full"
                    menuShouldScrollIntoView={true}
                    captureMenuScroll={true}
                    placeholder={t('common.type_at_least_3_characters')}
                    onMenuScrollToBottom={() => fetchNextPage()}
                    maxMenuHeight={200}
                    onInputChange={handleSearchChange}
                    inputId="hospital-select"
                    aria-label="hospital-select"
                    menuIsOpen={!!searchString?.length && searchString?.length >= 2}
                    components={{
                      NoOptionsMessage: () => (
                        <Button
                          type="text"
                          className="w-full text-start rounded-none"
                          icon={<PlusOutlined />}
                          onClick={onCreateCustomHospitalClick}
                          style={{ color: textColor, backgroundColor: bgColorLight }}
                        >
                          {t('sign_up_page.add_hospital')}
                        </Button>
                      ),
                    }}
                  />
                </AntdForm.Item>
              )}
            </AntdCol>
          </AntdRow> */}

      <AntdConfigProvider theme={{ token: { colorLink: textColor } }}>
        <AntdForm.Item
          label=""
          valuePropName="checked"
          name="privacyPolicy"
          rules={[
            {
              validator: (_, value) =>
                value ? Promise.resolve() : Promise.reject(new Error(t('validations.required'))),
            },
          ]}
        >
          <AntdCheckbox data-testid="privacy-policy" className="text-[10px] sm:text-sm">
            <Trans
              i18nKey={'privacy_policy.text'}
              components={[
                <a
                  key="privacy_link"
                  href={t('privacy_policy.link')}
                  rel="noreferrer"
                  target="_blank"
                  className="underline"
                />,
                <a
                  key="privacy_mail"
                  href="mailto:privacy@curewiki.health"
                  className="underline"
                />,
              ]}
            />
          </AntdCheckbox>
        </AntdForm.Item>
        <AntdForm.Item
          label=""
          valuePropName="checked"
          name="termsOfUse"
          rules={[
            {
              validator: (_, value) =>
                value ? Promise.resolve() : Promise.reject(new Error(t('validations.required'))),
            },
          ]}
        >
          <AntdCheckbox data-testid="terms-of-use" className="text-[10px] sm:text-sm">
            <Trans
              i18nKey="terms_of_use.text"
              components={[
                <a
                  href={t('terms_of_use.link')}
                  rel="noreferrer"
                  target="_blank"
                  key="terms"
                  className="underline"
                />,
              ]}
            />
          </AntdCheckbox>
        </AntdForm.Item>{' '}
      </AntdConfigProvider>

      <Text
        style={{ color: textColorDark, backgroundColor: bgColorLight }}
        className="py-2  px-4 rounded-default flex w-full justify-center mb-4"
      >
        {t('sign_up_page.discover_trials_text')}
      </Text>

      <AntdForm.Item className="mb-0">
        <Button
          className="w-full h-[48px] sm:h-auto"
          htmlType="submit"
          loading={isMutating}
          type="primary"
          data-testid="submit-button"
        >
          {t('sign_up_page.title')}
        </Button>

        <Button
          className="w-full mt-4 h-[48px] sm:h-auto"
          disabled={isMutating}
          type="default"
          onClick={handleBack}
          data-testid="back-button"
        >
          {t('common.back')}
        </Button>
      </AntdForm.Item>
    </AntdForm>
  );
};

export default UserInfoForm;
